import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type FetchNotificationsParams,
} from '@/api/notifications'
import type { NotificationListResponse } from '@/types/notification'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { toast } from '@/stores/useToastStore'
import { AxiosError } from 'axios'

function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    return error.response.data.detail
  }
  return 'An unexpected error occurred'
}

export function useNotifications(params: FetchNotificationsParams = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.list(
      params as unknown as Record<string, unknown>
    ),
    queryFn: () => fetchNotifications(params),
  })
}

export function useRecentNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications.list({
      limit: 5,
    } as unknown as Record<string, unknown>),
    queryFn: () => fetchNotifications({ limit: 5 }),
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  const decrementUnreadCount = useNotificationStore(s => s.decrementUnreadCount)

  return useMutation({
    mutationFn: ({ id, is_read }: { id: string; is_read: boolean }) =>
      markNotificationRead(id, is_read),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      })
      if (variables.is_read) {
        decrementUnreadCount()
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useMarkAllRead() {
  const queryClient = useQueryClient()
  const setUnreadCount = useNotificationStore(s => s.setUnreadCount)

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      // Optimistically update all notification query caches so the useEffect
      // in NotificationBell sees unread_count=0 immediately, preventing the
      // race where a stale refetch overwrites the store.
      queryClient.setQueriesData<NotificationListResponse>(
        { queryKey: queryKeys.notifications.all },
        old =>
          old
            ? {
                ...old,
                unread_count: 0,
                data: old.data.map(n => ({
                  ...n,
                  is_read: true,
                  read_at: n.read_at ?? new Date().toISOString(),
                })),
              }
            : old
      )
      setUnreadCount(0)
      // Background refetch to sync with server
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all,
      })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })
}
